import { CurrencyPipe } from '@angular/common';
import { Component, DestroyRef, Input, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, combineLatest, debounceTime, filter, startWith } from 'rxjs';

import { CurrencyMaskDirective } from '../../../../../lib/ngx-currency/src/currency-mask.directive';
import { FlexComponent } from '../../../../ui-kit/flex/flex.component';
import { EarlyPayCalculatorDaysSelectorComponent } from '../early-pay-calculator-days-selector/early-pay-calculator-days-selector.component';
import { EarlyPayCalculatorPlanToggleComponent } from '../early-pay-calculator-plan-toggle/early-pay-calculator-plan-toggle.component';

@Component({
  selector: 'app-early-pay-calculator',
  templateUrl: './early-pay-calculator.component.html',
  styleUrls: ['./early-pay-calculator.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FlexComponent,
    EarlyPayCalculatorPlanToggleComponent,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    CurrencyMaskDirective,
    ReactiveFormsModule,
    EarlyPayCalculatorDaysSelectorComponent,
    CurrencyPipe,
  ],
})
export class EarlyPayCalculatorComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() planToggleVisible = true;
  invoiceAmountControl: FormControl<number>;
  days$: BehaviorSubject<number> = new BehaviorSubject<number>(30);
  fee$: BehaviorSubject<number> = new BehaviorSubject<number>(0.004);
  upfrontFee$: BehaviorSubject<number> = new BehaviorSubject<number>(0.01);
  advancedPaymentAmount: number;
  feeAmount: number;
  savingAmount: number;
  savingPercent: number;
  finalPaymentAmount: number;
  platformFee: number;

  ngOnInit(): void {
    this.invoiceAmountControl = new FormControl<number>(50000, [Validators.min(1), Validators.max(500000000)]);

    this.watchValueChanges();
  }

  private watchValueChanges(): void {
    combineLatest([
      this.days$.asObservable(),
      this.fee$.asObservable(),
      this.upfrontFee$.asObservable(),
      this.invoiceAmountControl.valueChanges.pipe(startWith(this.invoiceAmountControl.value)),
    ])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.invoiceAmountControl.valid),
        debounceTime(300),
      )
      .subscribe(([days, subscriptionWeeklyFee, upfrontFee, amount]) => {
        const firstPaymentPart = amount * 0.75;
        const secondPaymentPart = amount * 0.25;
        this.platformFee = firstPaymentPart * upfrontFee;
        const discount = Math.ceil(((this.fee$.value * days) / 7) * 10000) / 10000;
        const eppFee = discount * firstPaymentPart;
        this.feeAmount = eppFee + this.platformFee;
        this.advancedPaymentAmount = firstPaymentPart - this.feeAmount;
        this.finalPaymentAmount = secondPaymentPart;

        const bluePlanDailyFee = 0.004 / 7;
        const bluePlanFee = firstPaymentPart * bluePlanDailyFee * days + firstPaymentPart * 0.01;
        this.savingAmount = bluePlanFee - this.feeAmount;
        this.savingPercent = (this.savingAmount / this.advancedPaymentAmount) * 100;
      });
  }

  updateDaysAmount(days: number): void {
    this.days$.next(days);
  }

  updateSubscriptionFee(data: { fee: number; upfrontFee: number }): void {
    this.fee$.next(data.fee);
    this.upfrontFee$.next(data.upfrontFee);
  }
}
